import React, { Component } from 'react';
import { StaticQuery, graphql, navigate } from 'gatsby';
import * as FormFields from './Fields';
import './leads.scss'

// Cloudflare Turnstile, explicit render. One script load shared by every form
// on the page — the CTA block and the newsletter sign-up both mount this
// component, and loading the script twice throws.
const TURNSTILE_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
let turnstileLoad = null

const loadTurnstile = () => {
  if (typeof window === `undefined`) return Promise.reject(new Error('No window'))
  if (window.turnstile) return Promise.resolve(window.turnstile)
  if (turnstileLoad) return turnstileLoad
  turnstileLoad = new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.src = TURNSTILE_SRC
    script.async = true
    script.defer = true
    script.onload = () => resolve(window.turnstile)
    script.onerror = () => {
      // Let a later attempt retry rather than caching the failure forever.
      turnstileLoad = null
      reject(new Error('Turnstile failed to load'))
    }
    document.head.appendChild(script)
  })
  return turnstileLoad
}

// The Lambda emails name, email, phone, company and message as labelled lines.
// Anything else the WordPress form JSON defines (a preferred contact method,
// say) is folded into the message so it still reaches the inbox rather than
// being silently dropped.
const NAMED_FIELDS = ['name', 'email', 'phone', 'company', 'message']

// Channel and campaign details from LeadSources (loaded via GTM), flattened to
// one line for the enquiry email so Scope can see where a lead came from
// without logging into LeadSources. Visitor and session ids are left out.
const LEAD_SOURCE_KEYS = /channel|source|medium|campaign|term|content|referr|landing/i

const leadSourceSummary = () => {
  try {
    const ls = typeof window !== `undefined` && window.leadsources
    if (!ls || typeof ls.getTrackingData !== 'function') return null
    const data = ls.getTrackingData() || {}
    const parts = Object.keys(data)
      .filter(key => LEAD_SOURCE_KEYS.test(key))
      .filter(key => ['string', 'number'].indexOf(typeof data[key]) !== -1 && `${data[key]}` !== '')
      .map(key => `${key}=${data[key]}`)
    return parts.length ? parts.join(', ') : null
  } catch (err) {
    console.error('LeadSources getTrackingData', err)
    return null
  }
}

class Index extends Component {

  turnstileRef = React.createRef();
  widgetId = null;
  tokenResolve = null;
  tokenReject = null;

  constructor(props) {
    const { thankYou = null, data, id = null, subscribe = null } = props
    const { allWordpressWpForm } = data

    let settings = id && allWordpressWpForm && allWordpressWpForm.nodes.filter(i => i['wordpress_id'] === parseInt(id) )
    settings = settings && settings[0] && settings[0].formJson && settings[0].formJson

    super(props);

    this.state = {
      error: null,
      loading: false,
      submitted: false,
      ready: false,
      readyStatus: true,
      id: id,
      thankYou: thankYou,
      settings: settings && settings.formFields && settings.formFields,
      lead: {
        formId: id,
        [`newsletter-subscribe`]: subscribe
      },
      // Off-screen honeypot. A real visitor never fills it; the Lambda answers
      // a filled one with a fake success so a bot gets no signal.
      honeypot: '',
      formHoveredOver: false,
    }
  }

  componentWillUnmount() {
    this.removeWidget()
  }

  removeWidget() {
    if (typeof window !== `undefined` && window.turnstile && this.widgetId !== null) {
      try { window.turnstile.remove(this.widgetId) } catch (e) { /* already gone */ }
    }
    this.widgetId = null
  }

  // Rendered on first interaction, not on page load, so the script is only
  // fetched by visitors who actually approach a form.
  mountWidget = async () => {
    if (this.widgetId !== null || !this.turnstileRef.current) return
    const siteKey = process.env.GATSBY_TURNSTILE_SITE_KEY
    if (!siteKey) {
      console.error('GATSBY_TURNSTILE_SITE_KEY is not set - the form cannot be verified')
      return
    }
    let turnstile
    try {
      turnstile = await loadTurnstile()
    } catch (err) {
      console.error(err)
      return
    }
    // The await above yields, so re-check: a second hover or an unmount may
    // have run in the meantime.
    if (this.widgetId !== null || !this.turnstileRef.current) return
    this.widgetId = turnstile.render(this.turnstileRef.current, {
      sitekey: siteKey,
      // Invisible until Cloudflare decides this visitor needs a challenge, and
      // only run when we ask for a token - matching the invisible reCAPTCHA
      // behaviour this replaces.
      execution: 'execute',
      appearance: 'interaction-only',
      callback: token => {
        const resolve = this.tokenResolve
        this.tokenResolve = null
        this.tokenReject = null
        resolve && resolve(token)
      },
      'error-callback': () => {
        const reject = this.tokenReject
        this.tokenResolve = null
        this.tokenReject = null
        reject && reject(new Error('Turnstile verification failed'))
      },
      'expired-callback': () => {
        const reject = this.tokenReject
        this.tokenResolve = null
        this.tokenReject = null
        reject && reject(new Error('Turnstile challenge expired'))
      },
    })
  }

  getToken = () => new Promise((resolve, reject) => {
    if (!window.turnstile || this.widgetId === null)
      return reject(new Error('Turnstile is not ready'))
    this.tokenResolve = resolve
    this.tokenReject = reject
    // Reset first so a second submission on the same page gets a fresh token:
    // a token is single-use and Cloudflare rejects a replay.
    window.turnstile.reset(this.widgetId)
    window.turnstile.execute(this.widgetId)
  })

  handleSubmit = async (e) => {
    e.preventDefault();
    if (this.state.loading) return

    this.setState({ loading: true, error: null })

    let token
    try {
      await this.mountWidget()
      token = await this.getToken()
    } catch (err) {
      console.error('Turnstile', err)
      this.setState({ loading: false, error: true, readyStatus: true })
      return
    }

    this.submitLead(token)
  }

  submitLead = (turnstileToken) => {
    const endpoint = process.env.GATSBY_FORM_ENDPOINT
    if (!endpoint) {
      console.error('GATSBY_FORM_ENDPOINT is not set - nowhere to send the form')
      this.setState({ loading: false, error: true })
      return
    }

    window.dataLayer = window.dataLayer || [];
    const { lead, honeypot } = this.state
    const { subscribe = null } = this.props
    const location = typeof window !== `undefined` ? window.location.href : ''

    // Fields the email template has no line for still need to reach the inbox,
    // so they are appended to the message body.
    const extras = Object.keys(lead)
      .filter(key => NAMED_FIELDS.indexOf(key) === -1 && key !== 'formId' && key !== 'newsletter-subscribe')
      .filter(key => lead[key] !== undefined && lead[key] !== null && `${lead[key]}` !== '')
      .map(key => `${key}: ${lead[key]}`)

    const source = leadSourceSummary()
    if (source) extras.push(`lead source: ${source}`)

    const message = [lead.message, extras.length ? extras.join('\n') : null]
      .filter(Boolean)
      .join('\n\n')

    const formName = subscribe ? 'newsletter sign-up' : 'contact enquiry'
    const payload = {
      siteId: 'scopeadvisory',
      formName: formName,
      pageUrl: location,
      name: lead.name,
      email: lead.email,
      phone: lead.phone,
      company: lead.company,
      message: message,
      website: honeypot,
      turnstileToken: turnstileToken,
    }

    fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
    .then(response => {
      if (response.ok) {
        window.dataLayer.push({
          'event': 'form_submit_success',
          'formLocation': location,
          'formId': this.state.id,
          'formName': formName
        });
        // LeadSources can't hijack a fetch-submitted React form, so the lead is
        // handed over explicitly. Its script is loaded via GTM; tracking must
        // never stand between the visitor and the thank-you page.
        try {
          if (window.leadsources && typeof window.leadsources.trackLead === 'function') {
            window.leadsources.trackLead({
              name: lead.name,
              email: lead.email,
              phone: lead.phone,
              company: lead.company,
              formName: formName,
            })
          }
        } catch (err) {
          console.error('LeadSources trackLead', err)
        }
        this.setState({ loading: false, submitted: true, lead: {} });
        this.props.thankYou && this.props.thankYou !== '' && navigate(this.props.thankYou)
      } else {
        this.setState({ loading: false, error: true });
      }
    })
    .catch(error => {
      console.error('submitForm Error', error)
      this.setState({ loading: false, error: true });
    });
  }

  //Form change
  handleChange(e) {
      let lead = this.state.lead
      //If file
      if (e.target.type === 'file') {

        let fileName = `lead-file-${e.target.name}`
        let file = e.target.files[0];
        lead[fileName] = { data: '', file: '' }

        if (file) {
          const reader = new FileReader(file);
          reader.readAsDataURL(file);
          reader.onload = () => {
            // set image and base64'd image data in component state
            lead[fileName].data = reader.result
            lead[fileName].file = file.name
          }
        }
      } else if ( e.target.type === 'checkbox' ) {
        //Toggle value on off
        lead[e.target.name] === e.target.value ? lead[e.target.name] = '' : lead[e.target.name] = e.target.value
      } else {
        //All other fields
        lead[e.target.name] = e.target.value
      }
      this.setState({lead})
  }

  handleFormHover() {
    if (!this.state.formHoveredOver) {
      this.setState({formHoveredOver: true}, () => this.mountWidget())
    } else {
      return null;
    }
  }


  render() {
    const { submitButton = 'Get started today', thankYouText = '<h3>Thank you!</h3><p>Your message has been sent.</p>' } = this.props
    const { loading, submitted, error, lead, id, settings, readyStatus, honeypot } = this.state

    if (!id) return 'No form id!'

    if (!settings) return 'There is a problem with the JSON!';

    if (submitted) return <div className="lead-form thank-you" dangerouslySetInnerHTML={{__html:thankYouText}} />

    const formatComponentName = (string) => {
      return string.charAt(0).toUpperCase() + string.slice(1);
    };

    return (
      <div className="lead-form" onMouseEnter={() => this.handleFormHover()} onTouchStart={() => this.handleFormHover()} onFocus={() => this.handleFormHover()}>
        {error && <div className="error"><div>There was an error submitting the form.</div></div>}
        {loading && <div className="loading"><div><div className="spin"/>Loading...</div></div>}
        <form className="lead-form-inner" onSubmit={event => {this.handleSubmit(event)}}>
          <div className="form-inner">
            {settings.map((field, index) => {
              const FormComponent = FormFields[formatComponentName(field.type)];
              // Map each field into the form component
              return (
                <FormComponent key={index} field={field} value={lead} handleChange={e => this.handleChange(e)} />
              )
            })}
            {/* Honeypot. Positioned off screen rather than display:none, which some bots skip. */}
            <div className="honeypot-field" aria-hidden="true">
              <label htmlFor={`website_url_${id}`}>Leave this field empty</label>
              <input
                type="text"
                id={`website_url_${id}`}
                name="website_url"
                tabIndex="-1"
                autoComplete="off"
                value={honeypot}
                onChange={e => this.setState({ honeypot: e.target.value })}
              />
            </div>
            <div className="captcha-wrapper">
              <div ref={this.turnstileRef} className="turnstile-widget" />
              <button disabled={!readyStatus || loading} className={`submit${this.state.ready ? ' ready' : ' ready'}`}>{submitButton}</button>
            </div>
          </div>
        </form>
      </div>
    )
  }
}


export default props => (
  <StaticQuery
    query={graphql`
      query {
        wordpressWpSettings {
          title
          siteUrl
        }
        allWordpressWpForm {
          nodes {
            wordpress_id
            formJson {
              recipientAddress
              formFields {
                type
                required
                placeholder
                options {
                  label
                  value
                }
                name
                className
              }
            }
          }
        }
      }
    `}
    render={data => <Index data={data} {...props} />}
  />
)
